import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Spinner, Select } from 'flowbite-react';
import { PlusIcon } from 'lucide-react';
import { FiArrowLeft, FiInfo } from 'react-icons/fi';
import { RxUpdate } from 'react-icons/rx';
import { GoTrash } from 'react-icons/go';
import { Drawer } from '../../../components/Drawer';
import Input from '../../../components/input';
import { useLocation, useNavigate } from 'react-router-dom';
import { apiEndPoints } from '../../../hooks/remote/apiEndPoints';
import { toast } from 'react-toastify';
import ConfirmDeleteModal from '../../../components/ConfirmDeleteModal';

const defaultValues = {
  cpu_threshold: '',
  ram_threshold: '',
  disk_threshold: '',
  notification_frequency_minutes: '',
  max_emails_per_day: '',
  statistic: 'average',
  send_email_to: '',
  email_ccs: '',
};

const statisticOptions = [
  {
    value: 'maximum',
    label: 'Maximum',
    description:
      'Triggers alarm when the highest value across 4 periods (last 20 minutes) exceeds threshold',
  },
  {
    value: 'average',
    label: 'Average',
    description:
      'Triggers alarm when the average value across 4 periods (last 20 minutes) exceeds threshold',
  },
  {
    value: 'minimum',
    label: 'Minimum',
    description:
      'Triggers alarm when the lowest value across 4 periods (last 20 minutes) exceeds threshold',
  },
];

const ServerAlarming = () => {
  document.title = 'Server Alarming - Cloudvantage';
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const pathArray = pathname.split('/');
  const projectId = pathArray[1];
  const serverId = pathArray[3];
  const [isFetching, setIsFetching] = useState(false);
  const [isUpdateOpen, setIsUpdateOpen] = useState(false);
  const [isNewOpen, setIsNewOpen] = useState(false);
  const [alarm, setAlarm] = useState(null);
  const [updatedAlarm, setUpdatedAlarm] = useState(alarm);
  const [newAlarm, setNewAlarm] = useState(defaultValues);
  const [isCreating, setIsCreating] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [showStatisticTooltip, setShowStatisticTooltip] = useState(false);

  const handleChange = (e) => {
    const { name, value, type } = e.target;
    const parsed =
      type === 'number' ? (value === '' ? '' : Number(value)) : value;
    setNewAlarm((prev) => ({ ...prev, [name]: parsed }));
  };

  const handleUpdateChange = (e) => {
    const { name, value, type } = e.target;
    const parsed =
      type === 'number' ? (value === '' ? '' : Number(value)) : value;
    setUpdatedAlarm((prev) => ({ ...prev, [name]: parsed }));
  };

  const handleFetchServerAlarming = useCallback(async () => {
    setIsFetching(true);
    try {
      const { data } = await apiEndPoints.getServerAlarmingDetails(
        projectId,
        serverId,
      );
      if (data && data.alarm === null) {
        setAlarm(null);
      } else {
        setAlarm(data.alarm);
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Error fetching alarm');
    } finally {
      setIsFetching(false);
    }
  }, [projectId, serverId]);

  const handleCreateAlarm = async (e) => {
    e.preventDefault();
    setIsCreating(true);
    try {
      await apiEndPoints.createServerAlarming(projectId, serverId, newAlarm);
      await handleFetchServerAlarming();
      setNewAlarm(defaultValues);
      toast.success('New Alarm created successfully');
      setIsNewOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error creating alarm');
    } finally {
      setIsCreating(false);
    }
  };

  const handleUpdateAlarm = async (e) => {
    const temp = {
      cpu_threshold: updatedAlarm.cpu_threshold,
      ram_threshold: updatedAlarm.ram_threshold,
      disk_threshold: updatedAlarm.disk_threshold,
      notification_frequency_minutes:
        updatedAlarm.notification_frequency_minutes,
      max_emails_per_day: updatedAlarm.max_emails_per_day,
      statistic: updatedAlarm.statistic,
      send_email_to: updatedAlarm.send_email_to,
      email_ccs: updatedAlarm.email_ccs,
    };
    e.preventDefault();
    setIsUpdating(true);
    try {
      await apiEndPoints.updateServerAlarming(projectId, serverId, temp);
      await handleFetchServerAlarming();
      setUpdatedAlarm(alarm);
      toast.success('Alarm updated successfully');
      setIsUpdateOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error updating alarm');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      const res = await apiEndPoints.deleteServerAlarming(projectId, serverId);
      setAlarm(null);
      toast.success(res.data.message);
      setIsDeleteModalOpen(false);
      window.location.reload();
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Error deleting alarm');
    } finally {
      setIsDeleting(false);
    }
  };

  useEffect(() => {
    handleFetchServerAlarming();
  }, [handleFetchServerAlarming]);

  const view = useMemo(() => {
    if (!alarm) return null;

    const pct = (v) =>
      typeof v === 'number' && !Number.isNaN(v) ? `${v}%` : '—';

    const num = (v) =>
      typeof v === 'number' && !Number.isNaN(v) ? String(v) : '—';

    return {
      resourceId: alarm.resource ?? '—',
      cpu: pct(alarm.cpu_threshold),
      ram:
        typeof alarm.ram_threshold === 'number'
          ? `${alarm.ram_threshold.toFixed(0)}%`
          : '—',
      disk:
        typeof alarm.disk_threshold === 'number'
          ? `${alarm.disk_threshold.toFixed(0)}%`
          : '—',
      createdOn: alarm.created_on || '—',
      updatedOn: alarm.updated_on || '—',
      lastNotifiedAt: alarm.last_notified_at || '—',
      frequencyMins: num(alarm.notification_frequency_minutes),
      maxEmails: num(alarm.max_emails_per_day),
      emailsToday: num(alarm.emails_sent_today),
      statistic: alarm.statistic || '—',
      sendEmailTo: alarm.send_email_to || '—',
      emailCcs: alarm.email_ccs || '—',
    };
  }, [alarm]);

  return (
    <div className="min-h-screen px-4 md:px-10 pt-6 pb-10 dark:text-white">
      {/* Header - always visible */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Button
            color=""
            size="sm"
            className="text-primaryBlue cursor-pointer"
            onClick={() => navigate(-1)}
          >
            <FiArrowLeft size={20} />
          </Button>
          <p className="crimson-font text-xl font-medium text-primaryBlue">
            Server Alarming
          </p>
        </div>
        <Button
          className="w-full sm:w-auto py-2 px-3 cursor-pointer bg-[#1da1f1]"
          size="sm"
          onClick={() => setIsNewOpen(true)}
        >
          <PlusIcon className="mr-2" />
          New Alarm
        </Button>
      </div>

      <div className="overflow-x-auto rounded-lg mt-4">
        {isFetching ? (
          <div className="flex justify-center py-8">
            <Spinner />
          </div>
        ) : view ? (
          // Alarm exists - show details
          <div className="space-y-6">
            {/* Header with actions */}
            <div className="rounded-lg bg-white border border-gray-200 p-4">
              <dl className="grid grid-cols-1 gap-4 md:grid-cols-3">
                <div>
                  <dt className="text-xs uppercase tracking-wide text-gray-500">
                    Resource ID
                  </dt>
                  <dd className="mt-1 text-sm">{view.resourceId}</dd>
                </div>
                <div>
                  <Button
                    className="w-full sm:w-auto py-2 px-3 cursor-pointer bg-white text-primaryBlue border border-primaryBlue hover:bg-primaryBlue hover:text-white"
                    size="sm"
                    onClick={() => {
                      setUpdatedAlarm(alarm);
                      setIsUpdateOpen(true);
                    }}
                  >
                    <RxUpdate className="mr-2" />
                    Update Alarm
                  </Button>
                </div>
                <div className="md:col-span-1">
                  <Button
                    className="w-full sm:w-auto py-2 px-3 cursor-pointer bg-white text-red-600 border border-red-600 hover:bg-red-600 hover:text-white"
                    size="sm"
                    onClick={() => setIsDeleteModalOpen(true)}
                  >
                    <GoTrash className="mr-2" />
                    Delete Alarm
                  </Button>
                </div>
              </dl>
            </div>

            {/* Thresholds */}
            <section>
              <h4 className="mb-3 text-sm font-semibold text-gray-700">
                Thresholds
              </h4>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                <div className="rounded-lg border bg-white border-gray-200 p-4">
                  <dl>
                    <dt className="text-xs uppercase tracking-wide text-gray-500">
                      CPU threshold
                    </dt>
                    <dd className="mt-1 text-sm">{view.cpu}</dd>
                  </dl>
                </div>
                <div className="rounded-lg border bg-white border-gray-200 p-4">
                  <dl>
                    <dt className="text-xs uppercase tracking-wide text-gray-500">
                      RAM threshold
                    </dt>
                    <dd className="mt-1 text-sm">{view.ram}</dd>
                  </dl>
                </div>
                <div className="rounded-lg border bg-white border-gray-200 p-4">
                  <dl>
                    <dt className="text-xs uppercase tracking-wide text-gray-500">
                      Disk threshold
                    </dt>
                    <dd className="mt-1 text-sm">{view.disk}</dd>
                  </dl>
                </div>
              </div>
            </section>

            {/* Statistics & Email Settings */}
            <section>
              <h4 className="mb-3 text-sm font-semibold text-gray-700">
                Configuration
              </h4>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div className="rounded-lg border bg-white border-gray-200 p-4">
                  <dl>
                    <dt className="text-xs uppercase tracking-wide text-gray-500">
                      Statistic Type
                    </dt>
                    <dd className="mt-1 text-sm capitalize">
                      {view.statistic}
                    </dd>
                  </dl>
                </div>
                <div className="rounded-lg border bg-white border-gray-200 p-4">
                  <dl>
                    <dt className="text-xs uppercase tracking-wide text-gray-500">
                      Send Email To
                    </dt>
                    <dd className="mt-1 text-sm">{view.sendEmailTo}</dd>
                  </dl>
                </div>
                <div className="rounded-lg border bg-white border-gray-200 p-4 md:col-span-2">
                  <dl>
                    <dt className="text-xs uppercase tracking-wide text-gray-500">
                      CC Emails
                    </dt>
                    <dd className="mt-1 text-sm">{view.emailCcs}</dd>
                  </dl>
                </div>
              </div>
            </section>

            {/* Notifications */}
            <section>
              <h4 className="mb-3 text-sm font-semibold text-gray-700">
                Notifications
              </h4>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                <div className="rounded-lg border bg-white border-gray-200 p-4">
                  <dl>
                    <dt className="text-xs uppercase tracking-wide text-gray-500">
                      Frequency
                    </dt>
                    <dd className="mt-1 text-sm">
                      Every {view.frequencyMins} minute(s)
                    </dd>
                  </dl>
                </div>
                <div className="rounded-lg border bg-white border-gray-200 p-4">
                  <dl>
                    <dt className="text-xs uppercase tracking-wide text-gray-500">
                      Emails today
                    </dt>
                    <dd className="mt-1 text-sm">
                      {view.emailsToday} / {view.maxEmails}
                    </dd>
                  </dl>
                </div>
                <div className="rounded-lg border bg-white border-gray-200 p-4">
                  <dl>
                    <dt className="text-xs uppercase tracking-wide text-gray-500">
                      Last notified
                    </dt>
                    <dd className="mt-1 text-sm">{view.lastNotifiedAt}</dd>
                  </dl>
                </div>
              </div>
            </section>

            {/* Timestamps */}
            <section>
              <h4 className="mb-3 text-sm font-semibold text-gray-700">
                Timestamps
              </h4>
              <div className="rounded-lg border bg-white border-gray-200 p-4">
                <dl className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                  <div>
                    <dt className="text-xs uppercase tracking-wide text-gray-500">
                      Created
                    </dt>
                    <dd className="mt-1 text-sm">{view.createdOn}</dd>
                  </div>
                  <div>
                    <dt className="text-xs uppercase tracking-wide text-gray-500">
                      Updated
                    </dt>
                    <dd className="mt-1 text-sm">{view.updatedOn}</dd>
                  </div>
                </dl>
              </div>
            </section>
          </div>
        ) : (
          // No alarm exists - show empty state
          <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-gray-300 bg-white p-12 text-center">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-blue-100">
              <PlusIcon className="h-6 w-6 text-primaryBlue" />
            </div>
            <h3 className="mt-4 text-sm font-semibold text-gray-900">
              No alarm configured
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              Get started by creating a new server alarm.
            </p>
            <Button
              className="mt-6 py-2 px-3 cursor-pointer bg-primaryBlue"
              size="sm"
              onClick={() => setIsNewOpen(true)}
            >
              <PlusIcon className="mr-2 h-4 w-4" />
              New Alarm
            </Button>
          </div>
        )}
      </div>

      <Drawer
        title="New Alarm"
        open={isNewOpen}
        onClose={() => setIsNewOpen(false)}
      >
        <form
          className="flex max-w-xl flex-col gap-4"
          onSubmit={handleCreateAlarm}
        >
          <Input
            label="CPU Threshold (%)"
            id="cpu_threshold"
            name="cpu_threshold"
            type="number"
            placeholder="0"
            value={newAlarm.cpu_threshold}
            onChange={handleChange}
            min={0}
            max={100}
            step={1}
            required
          />

          <Input
            label="RAM Threshold (%)"
            id="ram_threshold"
            name="ram_threshold"
            type="number"
            placeholder="0"
            value={newAlarm.ram_threshold}
            onChange={handleChange}
            min={0}
            max={100}
            step={1}
            required
          />

          <Input
            label="Disk Threshold (%)"
            id="disk_threshold"
            name="disk_threshold"
            type="number"
            placeholder="0"
            value={newAlarm.disk_threshold}
            onChange={handleChange}
            min={0}
            max={100}
            step={1}
            required
          />

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label
                htmlFor="notification_frequency_minutes"
                className="mb-2 flex items-center gap-2 text-sm font-medium text-gray-900"
              >
                Notification Frequency (minutes)
                <div className="relative group">
                  <FiInfo className="h-4 w-4 text-gray-400 cursor-help" />
                  <div className="absolute left-0 top-6 z-50 w-64 rounded-lg border border-gray-200 bg-white p-2 shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all">
                    <p className="text-xs text-gray-600">
                      Minimum number of minutes between successive email alerts.
                    </p>
                  </div>
                </div>
              </label>
              <Input
                id="notification_frequency_minutes"
                name="notification_frequency_minutes"
                type="number"
                placeholder="0"
                value={newAlarm.notification_frequency_minutes}
                onChange={handleChange}
                min={1}
                step={1}
                required
              />
            </div>

            <Input
              label="Max Emails per Day"
              id="max_emails_per_day"
              name="max_emails_per_day"
              type="number"
              placeholder="0"
              value={newAlarm.max_emails_per_day}
              onChange={handleChange}
              min={0}
              step={1}
              required
            />
          </div>

          <Select
            label="Choose a preset"
            id="freqPreset"
            onChange={(e) =>
              setNewAlarm((prev) => ({
                ...prev,
                notification_frequency_minutes: Number(
                  e.target.value || prev.notification_frequency_minutes,
                ),
              }))
            }
            className="mb-2"
          >
            <option value="">Select a preset</option>
            <option value="5">Every 5 minutes</option>
            <option value="15">Every 15 minutes</option>
            <option value="30">Every 30 minutes</option>
            <option value="60">Every 60 minutes</option>
          </Select>

          {/* Statistic Type with Info Icon */}
          <div className="relative">
            <label
              htmlFor="statistic"
              className="mb-2 flex items-center gap-2 text-sm font-medium text-gray-900"
            >
              Statistic Type
              <div className="relative group">
                <FiInfo
                  className="h-4 w-4 text-gray-400 cursor-help"
                  onMouseEnter={() => setShowStatisticTooltip(true)}
                  onMouseLeave={() => setShowStatisticTooltip(false)}
                />
                {showStatisticTooltip && (
                  <div className="absolute left-0 top-6 z-50 w-72 rounded-lg border border-gray-200 bg-white p-3 shadow-lg">
                    <div className="space-y-2 text-xs">
                      {statisticOptions.map((opt) => (
                        <div key={opt.value}>
                          <div className="font-semibold text-gray-900">
                            {opt.label}
                          </div>
                          <div className="text-gray-600">{opt.description}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </label>
            <Select
              id="statistic"
              name="statistic"
              value={newAlarm.statistic}
              onChange={handleChange}
              required
            >
              {statisticOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Select>
          </div>

          {/* Email Fields */}
          <Input
            label="Send Email To"
            id="send_email_to"
            name="send_email_to"
            type="email"
            placeholder="recipient@example.com"
            value={newAlarm.send_email_to}
            onChange={handleChange}
            required
          />

          <div>
            <label
              htmlFor="email_ccs"
              className="mb-2 block text-sm font-medium text-gray-900"
            >
              CC Emails{' '}
              <span className="text-gray-500 text-xs">(Optional)</span>
            </label>
            <Input
              id="email_ccs"
              name="email_ccs"
              type="text"
              placeholder="email1@example.com,email2@example.com"
              value={newAlarm.email_ccs}
              onChange={handleChange}
            />
            <p className="mt-1 text-xs text-gray-500">
              Separate multiple emails with commas
            </p>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button
              color="light"
              type="button"
              onClick={() => {
                setNewAlarm(defaultValues);
                setIsNewOpen(false);
              }}
              className="rounded border border-gray-300 px-3 py-2 text-sm hover:bg-gray-50"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isCreating}
              className="rounded bg-primaryBlue px-3 py-2 text-sm text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {isCreating ? 'Creating...' : 'Create'}
            </Button>
          </div>
        </form>
      </Drawer>

      <Drawer
        title="Update Alarm"
        open={isUpdateOpen}
        onClose={() => setIsUpdateOpen(false)}
      >
        <form
          className="flex max-w-xl flex-col gap-4"
          onSubmit={handleUpdateAlarm}
        >
          <Input
            label="CPU Threshold (%)"
            id="cpu_threshold"
            name="cpu_threshold"
            type="number"
            placeholder="0"
            value={updatedAlarm?.cpu_threshold}
            onChange={handleUpdateChange}
            min={0}
            max={100}
            step={1}
            required
          />

          <Input
            label="RAM Threshold (%)"
            id="ram_threshold"
            name="ram_threshold"
            type="number"
            placeholder="0"
            value={updatedAlarm?.ram_threshold}
            onChange={handleUpdateChange}
            min={0}
            max={100}
            step={1}
            required
          />

          <Input
            label="Disk Threshold (%)"
            id="disk_threshold"
            name="disk_threshold"
            type="number"
            placeholder="0"
            value={updatedAlarm?.disk_threshold}
            onChange={handleUpdateChange}
            min={0}
            max={100}
            step={1}
            required
          />

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label
                htmlFor="notification_frequency_minutes_update"
                className="mb-2 flex items-center gap-2 text-sm font-medium text-gray-900"
              >
                Notification Frequency (minutes)
                <div className="relative group">
                  <FiInfo className="h-4 w-4 text-gray-400 cursor-help" />
                  <div className="absolute left-0 top-6 z-50 w-64 rounded-lg border border-gray-200 bg-white p-2 shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all">
                    <p className="text-xs text-gray-600">
                      Minimum number of minutes between successive email alerts.
                    </p>
                  </div>
                </div>
              </label>
              <Input
                id="notification_frequency_minutes_update"
                name="notification_frequency_minutes"
                type="number"
                placeholder="0"
                value={updatedAlarm?.notification_frequency_minutes}
                onChange={handleUpdateChange}
                min={1}
                step={1}
                required
              />
            </div>

            <Input
              label="Max Emails per Day"
              id="max_emails_per_day"
              name="max_emails_per_day"
              type="number"
              placeholder="0"
              value={updatedAlarm?.max_emails_per_day}
              onChange={handleUpdateChange}
              min={0}
              step={1}
              required
            />
          </div>

          <Select
            label="Choose a preset"
            id="freqPreset"
            onChange={(e) =>
              setUpdatedAlarm((prev) => ({
                ...prev,
                notification_frequency_minutes: Number(
                  e.target.value || prev.notification_frequency_minutes,
                ),
              }))
            }
            value={updatedAlarm?.notification_frequency_minutes}
            className="mb-2"
          >
            <option value="">Select a preset</option>
            <option value="5">Every 5 minutes</option>
            <option value="15">Every 15 minutes</option>
            <option value="30">Every 30 minutes</option>
            <option value="60">Every 60 minutes</option>
          </Select>

          {/* Statistic Type with Info Icon */}
          <div className="relative">
            <label
              htmlFor="statistic_update"
              className="mb-2 flex items-center gap-2 text-sm font-medium text-gray-900"
            >
              Statistic Type
              <div className="relative group">
                <FiInfo
                  className="h-4 w-4 text-gray-400 cursor-help"
                  onMouseEnter={() => setShowStatisticTooltip(true)}
                  onMouseLeave={() => setShowStatisticTooltip(false)}
                />
                {showStatisticTooltip && (
                  <div className="absolute left-0 top-6 z-50 w-72 rounded-lg border border-gray-200 bg-white p-3 shadow-lg">
                    <div className="space-y-2 text-xs">
                      {statisticOptions.map((opt) => (
                        <div key={opt.value}>
                          <div className="font-semibold text-gray-900">
                            {opt.label}
                          </div>
                          <div className="text-gray-600">{opt.description}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </label>
            <Select
              id="statistic_update"
              name="statistic"
              value={updatedAlarm?.statistic || 'average'}
              onChange={handleUpdateChange}
              required
            >
              {statisticOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Select>
          </div>

          {/* Email Fields */}
          <Input
            label="Send Email To"
            id="send_email_to"
            name="send_email_to"
            type="email"
            placeholder="recipient@example.com"
            value={updatedAlarm?.send_email_to || ''}
            onChange={handleUpdateChange}
            required
          />

          <div>
            <label
              htmlFor="email_ccs_update"
              className="mb-2 block text-sm font-medium text-gray-900"
            >
              CC Emails{' '}
              <span className="text-gray-500 text-xs">(Optional)</span>
            </label>
            <Input
              id="email_ccs_update"
              name="email_ccs"
              type="text"
              placeholder="email1@example.com,email2@example.com"
              value={updatedAlarm?.email_ccs || ''}
              onChange={handleUpdateChange}
            />
            <p className="mt-1 text-xs text-gray-500">
              Separate multiple emails with commas
            </p>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button
              color="light"
              type="button"
              onClick={() => {
                setUpdatedAlarm(alarm);
                setIsUpdateOpen(false);
              }}
              className="rounded border border-gray-300 px-3 py-2 text-sm hover:bg-gray-50"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isUpdating}
              className="rounded bg-primaryBlue px-3 py-2 text-sm text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {isUpdating ? 'Updating...' : 'Update'}
            </Button>
          </div>
        </form>
      </Drawer>

      <ConfirmDeleteModal
        open={isDeleteModalOpen}
        title="Delete Alarm"
        name={view?.resourceId || 'Alarm'}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleDelete}
        description="To delete this alarm, type the resource ID to confirm"
        isDeleting={isDeleting}
      />
    </div>
  );
};

export default ServerAlarming;
